<template>
  <CollapsableSection
    :class="$style.collapsableSection"
    :collapse="false"
    title="Details"
    :tooltipKbLink="KbArticleUrls.systemInsights"
    tooltipContent="Select a System Insights table to see more details."
  >
    <div :class="$style.detailsControls">
      <DropDownMenu
        :class="$style.dropDownControl"
        :isLargeDropDown="false"
        :leftAlignList="true"
        :menuSections="menuSections"
        :menuTitle="menuTitle"
        menuFontSize="small"
        :showMenuLabel="true"
        :useButtonToActivate="true"
      />
      <FlatActionButton
        :action="exportData"
        :disabled="!ableToExportMetricGroupData(currentMetricGroup)"
        :loading="showExportButtonLoading"
        text="export"
      />
    </div>
    <div :class="$style.tableContainer">
      <InsightsMultiItemTable
        v-if="tableColumns"
        :metricGroup="currentMetricGroup"
        :systemId="item.id"
      />
      <InsightsSingleItemTable
        v-else
        :metricGroup="currentMetricGroup"
        :systemId="item.id"
      />
    </div>
  </CollapsableSection>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import DropDownMenu from 'components/DropDownMenu/DropDownMenu.vue';
import EventManager from 'client/GrowthEngineering/EventManager';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import InsightsMultiItemTable from 'client/systems/systemInsights/components/InsightsMultiItemTable.vue';
import InsightsSingleItemTable from 'client/systems/systemInsights/components/InsightsSingleItemTable.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import SystemInsightsMetadata from 'client/systems/systemInsights/SystemInsightsMetadata';

const {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
  SystemInsightsConsts,
} = Constants;
const {
  systemAndHardware,
  operatingSystem,
  software,
  network,
  usersAndGroups,
} = SystemInsightsConsts.groups;
const {
  error,
  linux,
  mac,
  windows,
} = Constants.default;
const storeModel = StoreModelMap[ItemTypeConsts.system];
const systemInsightsModule = StoreModuleMap[ItemTypeConsts.systemInsights];
const { getMetricGroupsByOs, metricGroupMetadata, metricGroups } = SystemInsightsMetadata;

export default {
  name: 'InsightsDetails',
  components: {
    CollapsableSection,
    DropDownMenu,
    FlatActionButton,
    InsightsMultiItemTable,
    InsightsSingleItemTable,
  },

  props: {
    item: {
      required: true,
      type: Object,
    },
  },

  data: () => ({
    KbArticleUrls,
    currentMetricGroup: '',
    showExportButtonLoading: false,
    showLoadingOverlay: false,
  }),

  computed: {
    ...mapGetters(storeModel, {
      isMacOS: 'isMacOS',
      isWindows: 'isWindows',
    }),

    ...mapGetters(systemInsightsModule, [
      'ableToExportMetricGroupData',
    ]),

    menuSections() {
      return [
        {
          menuLabel: systemAndHardware,
          menuItems: this.getMenuItems(systemAndHardware),
        },
        {
          menuLabel: operatingSystem,
          menuItems: this.getMenuItems(operatingSystem),
        },
        {
          menuLabel: software,
          menuItems: this.getMenuItems(software),
        },
        {
          menuLabel: network,
          menuItems: this.getMenuItems(network),
        },
        {
          menuLabel: usersAndGroups,
          menuItems: this.getMenuItems(usersAndGroups),
        },
      ];
    },

    menuTitle() {
      return metricGroupMetadata[this.currentMetricGroup].label;
    },

    os() {
      if (this.isWindows) return windows;
      if (this.isMacOS) return mac;
      return linux;
    },

    tableColumns() {
      return metricGroupMetadata[this.currentMetricGroup].columns;
    },
  },

  created() {
    // Set which dropdown options should be shown based on the device OS
    this.dropdownOptions = getMetricGroupsByOs(this.os);

    // Set the current metric as systemInfo
    this.currentMetricGroup = metricGroups.systemInfo;
  },

  methods: {
    ...mapActions(systemInsightsModule, [
      'exportInsightsData',
    ]),

    exportData() {
      this.showExportButtonLoading = true;
      this.exportInsightsData({
        id: this.item.id,
        metricGroup: this.currentMetricGroup,
      })
        .catch(() => {
          notify(error, 'Export unsuccessful');
        })
        .finally(() => {
          this.showExportButtonLoading = false;
        });
      this.triggerExportEvent();
    },

    getMenuItems(group) {
      const menuItems = this.dropdownOptions.reduce((acc, metricGroup) => {
        if (metricGroupMetadata[metricGroup].group === group) {
          acc.push({
            label: metricGroupMetadata[metricGroup].label,
            action: () => this.selectTable(metricGroup),
          });
        }
        return acc;
      }, []);
      menuItems.sort((a, b) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      });
      return menuItems.length ? menuItems : [
        {
          label: 'Data available in API only',
          action: () => {},
          disabled: true,
        },
      ];
    },

    selectTable(metricGroup) {
      this.currentMetricGroup = metricGroup;
      this.triggerTableViewEvent();
    },

    triggerExportEvent() {
      const event = {
        os: this.item.os,
        orgId: this.item.organization,
        systemId: this.item.id,
        selectedTable: this.currentMetricGroup,
      };
      EventManager.triggerSystemInsightsExportClicked(event);
    },

    triggerTableViewEvent() {
      const event = {
        os: this.item.os,
        orgId: this.item.organization,
        systemId: this.item.id,
        selectedTable: this.currentMetricGroup,
      };
      EventManager.triggerSystemInsightsTableSelected(event);
    },
  },
};
</script>

<style scoped module>
.collapsableSection { overflow: visible; }

.inputLabel {
  color: gradientDark2;
  font-size: 1rem;
  font-weight: 700 !important;
}

.detailsControls {
  display: flex;
  height: 29px;
}

.dropDownControl {
  padding-right: var(--jc-spacer-small);
}

.tableContainer {
  position: relative;
}
</style>
